import React, { useEffect, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { change, Field, formValueSelector } from 'redux-form'
import { Select, Textarea } from '../../../../../../../../../../itrust_common/components/form'
import { required } from '../../../../../../../../../../itrust_common/components/form/FormValidations'
import {
  eyemedLenDesignLoad,
  eyemedLenMaterialLoad,
  eyemedSpecialParametersLoad,
} from '../../../../../../../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceClaimFormAction'
import SpecialParametersFields from './SpecialParametersFields'

const FORM = 'EyeMedClaimForm'

const FRAME_DIAGNOSIS_OPTIONS = [
  { label: 'Z01.00 - Encounter for examination of eyes and vision without abnormal findings', value: 'Z01.00' },
]

const LENS_TYPE_OPTIONS = {
  withoutAdd: [{ label: 'Single Vision', value: 'Single Vision' }],
  withAdd: [
    { label: 'BiFocal', value: 'BiFocal' },
    { label: 'Progressive', value: 'Progressive' },
    { label: 'TriFocal', value: 'TriFocal' },
  ],
}

const LENS_TYPE_CODE = {
  'Single Vision': '1',
  'BiFocal': '2',
  'Progressive': '3',
  'TriFocal': '4',
}

function toOptions(list) {
  if (!Array.isArray(list)) return []
  return list.map((item) => {
    if (typeof item === 'string') return { label: item, value: item }
    const value = item.value || item.code || item.name || item.label
    return { label: item.label || item.name || value, value }
  }).filter((item) => item.value)
}

function toMaterialOptions(list) {
  if (!Array.isArray(list)) return []
  return list.map((item) => {
    if (typeof item === 'string') return { label: item, value: item, index: '' }
    const value = item.value || item.code || item.lensMaterial || item.name || item.label
    return {
      label: item.label || item.name || item.lensMaterial || value,
      value,
      index: item.index ?? item.highIndex ?? '',
    }
  }).filter((item) => item.value)
}

function getLabParams(lab = {}) {
  return {
    supplierLocationId: lab.supplier_location_id || '',
    labAccessGroups: lab.lab_access_groups || '',
    labProgramCode: lab.lab_program_code || '',
    labTxit: lab.lab_txit || '',
    jobType: 'FTC',
  }
}

function LensDesignMaterialFields({
  hasAddValue,
  claimId,
  lensType,
  lensDesign,
  lensMaterial,
  progressiveManufacturer,
  progressiveProduct,
  laboratory,
  availableLabs,
}) {
  const dispatch = useDispatch()
  const [lenDesigns, setLenDesigns] = useState([])
  const [materialOptions, setMaterialOptions] = useState([])
  const [designLoading, setDesignLoading] = useState(false)
  const [materialLoading, setMaterialLoading] = useState(false)
  const [specialLoading, setSpecialLoading] = useState(false)
  const [showSpecialParams, setShowSpecialParams] = useState(false)
  const [specialParams, setSpecialParams] = useState([])

  const isProgressive = lensType === 'Progressive'
  const lensTypeCode = LENS_TYPE_CODE[lensType]
  const lab = laboratory || availableLabs[0] || {}
  const labParams = getLabParams(lab)
  const labKey = lab.supplier_location_id || lab.lab_program_code || lab.account || ''
  const designCode = isProgressive ? progressiveProduct : lensDesign
  const selectedMaterial = materialOptions.find((item) => item.value === lensMaterial)
  const highIndex = selectedMaterial?.index ?? ''

  const manufacturerOptions = [...new Set(lenDesigns.map((d) => d.manufacturer).filter(Boolean))]
    .map((name) => ({ label: name, value: name }))

  const productOptions = lenDesigns
    .filter((d) => d.manufacturer === progressiveManufacturer && d.code)
    .map((d) => ({ label: d.name || d.code, value: d.code }))

  const selectedDesign = lenDesigns.find((d) => d.code === progressiveProduct)
  const lensDesignParam = selectedDesign?.lensDesign || selectedDesign?.design || designCode

  const clearField = (name) => dispatch(change(FORM, `lenses.${name}`, ''))
  const prevLensTypeCode = useRef()
  const prevDesignCode = useRef()
  const prevManufacturer = useRef(progressiveManufacturer)

  // Load designs when lens type / lab (from redux) is ready
  useEffect(() => {
    if (!claimId || !lensTypeCode || !labKey) {
      setLenDesigns([])
      return
    }

    const lensTypeChanged = prevLensTypeCode.current && prevLensTypeCode.current !== lensTypeCode
    prevLensTypeCode.current = lensTypeCode
    if (lensTypeChanged) {
      ;['lens_design', 'lens_material', 'progressive_manufacturer', 'progressive_product'].forEach(clearField)
      setShowSpecialParams(false)
      setSpecialParams([])
    }

    setDesignLoading(true)
    dispatch(eyemedLenDesignLoad(claimId, { lensType: lensTypeCode, ...labParams }))
      .then((res) => {
        const data = res?.value?.data?.data
        setLenDesigns(data?.lendesign || (Array.isArray(data) ? data : []))
      })
      .catch(() => setLenDesigns([]))
      .finally(() => setDesignLoading(false))
  }, [claimId, lensTypeCode, labKey, dispatch])

  // Clear product when manufacturer changes (skip first mount / navigate back)
  useEffect(() => {
    if (prevManufacturer.current === progressiveManufacturer) return
    prevManufacturer.current = progressiveManufacturer
    clearField('progressive_product')
    clearField('lens_material')
    setShowSpecialParams(false)
    setSpecialParams([])
  }, [progressiveManufacturer])

  // Load materials when design / progressive product is selected
  useEffect(() => {
    const ready = claimId && lensTypeCode && labKey && designCode && (!isProgressive || progressiveManufacturer)
    if (!ready) {
      setMaterialOptions([])
      return
    }

    const designChanged = prevDesignCode.current && prevDesignCode.current !== designCode
    prevDesignCode.current = designCode
    if (designChanged) {
      clearField('lens_material')
      setShowSpecialParams(false)
      setSpecialParams([])
    }

    setMaterialLoading(true)
    const filters = {
      lensType: lensTypeCode,
      lensDesign: designCode,
      ...labParams,
      ...(isProgressive && {
        progressiveManufacturer,
        progressiveProduct: designCode,
      }),
    }

    dispatch(eyemedLenMaterialLoad(claimId, filters))
      .then((res) => setMaterialOptions(toMaterialOptions(res?.value?.data?.data?.lenmaterial || res?.value?.data?.data)))
      .catch(() => setMaterialOptions([]))
      .finally(() => setMaterialLoading(false))
  }, [claimId, lensTypeCode, designCode, isProgressive, progressiveManufacturer, labKey, dispatch])

  // Load special parameters when progressive fields are complete
  useEffect(() => {
    const ready = isProgressive
      && claimId
      && lensTypeCode
      && labKey
      && progressiveManufacturer
      && progressiveProduct
      && lensMaterial

    if (!ready) {
      setShowSpecialParams(false)
      setSpecialParams([])
      return
    }

    setSpecialLoading(true)
    setShowSpecialParams(true)
    setSpecialParams([])

    dispatch(eyemedSpecialParametersLoad(claimId, {
      lensType: lensTypeCode,
      lensDesign: lensDesignParam,
      lensMaterial,
      highIndex,
      progressiveManufacturer,
      progressiveProduct,
      ...labParams,
    }))
      .then((res) => {
        const list = res?.value?.data?.data?.specialparameters
        const params = Array.isArray(list) ? list : []
        setSpecialParams(params)
        params.forEach((param) => {
          if (!param?.xmlParameterName) return
          const value = param.defaultValue === 0 || param.defaultValue ? param.defaultValue : ''
          dispatch(change(FORM, `lenses.special_parameters.${param.xmlParameterName}`, value))
        })
      })
      .catch(() => setSpecialParams([]))
      .finally(() => setSpecialLoading(false))
  }, [
    isProgressive,
    claimId,
    lensTypeCode,
    progressiveManufacturer,
    progressiveProduct,
    lensMaterial,
    highIndex,
    lensDesignParam,
    labKey,
    dispatch,
  ])

  return (
    <>
      <div className="border rounded p-5 mt-4">
        <h5 className="mb-4 font-weight-bold">Lens Design & Material</h5>
        <div className="col-10 px-0">
          <div className="d-flex align-items-center mb-4">
            <label className="mb-0 mr-3 text-nowrap" style={{ minWidth: '8.5rem' }}>Lens Type:</label>
            <Field
              name="lens_type"
              component={Select}
              data={hasAddValue ? LENS_TYPE_OPTIONS.withAdd : LENS_TYPE_OPTIONS.withoutAdd}
              textField="label"
              valueField="value"
              placeholder="--- Select a Lens Type ---"
              className="custom-select"
              wrapperClasses="mb-0 flex-grow-1"
              key={hasAddValue ? 'with-add' : 'without-add'}
              validate={[required]}
            />
            <span className="text-danger ml-2">*</span>
          </div>

          {isProgressive ? (
            <>
              <div className="d-flex align-items-center mb-4">
                <label className="mb-0 mr-3 text-nowrap" style={{ minWidth: '11rem' }}>Progressive Manufacturer:</label>
                <Field
                  name="progressive_manufacturer"
                  component={Select}
                  data={manufacturerOptions}
                  textField="label"
                  valueField="value"
                  placeholder="--- Select a Progressive Manufacturer ---"
                  className="custom-select"
                  wrapperClasses="mb-0 flex-grow-1"
                  disabled={designLoading}
                  validate={[required]}
                />
                <span className="text-danger ml-2">*</span>
                {designLoading && <span className="spinner-border spinner-border-sm text-primary ml-2" />}
              </div>
              <div className="d-flex align-items-center mb-4">
                <label className="mb-0 mr-3 text-nowrap" style={{ minWidth: '11rem' }}>Progressive Product:</label>
                <Field
                  name="progressive_product"
                  component={Select}
                  data={productOptions}
                  textField="label"
                  valueField="value"
                  placeholder="--- Select a Progressive Product ---"
                  className="custom-select"
                  wrapperClasses="mb-0 flex-grow-1"
                  disabled={designLoading || !progressiveManufacturer}
                  validate={[required]}
                />
                <span className="text-danger ml-2">*</span>
              </div>
            </>
          ) : (
            <div className="d-flex align-items-center mb-4">
              <label className="mb-0 mr-3 text-nowrap" style={{ minWidth: '8.5rem' }}>Lens Design:</label>
              <Field
                name="lens_design"
                component={Select}
                data={toOptions(lenDesigns)}
                textField="label"
                valueField="value"
                placeholder="--- Select a Lens Design ---"
                className="custom-select"
                wrapperClasses="mb-0 flex-grow-1"
                disabled={designLoading}
                validate={[required]}
              />
              <span className="text-danger ml-2">*</span>
              {designLoading && <span className="spinner-border spinner-border-sm text-primary ml-2" />}
            </div>
          )}

          <div className="d-flex align-items-center mb-4">
            <label className="mb-0 mr-3 text-nowrap" style={{ minWidth: '8.5rem' }}>Lens Material:</label>
            <Field
              name="lens_material"
              component={Select}
              data={materialOptions}
              textField="label"
              valueField="value"
              placeholder="--- Select a Lens Material ---"
              className="custom-select"
              wrapperClasses="mb-0 flex-grow-1"
              disabled={materialLoading || !designCode}
              validate={[required]}
            />
            <span className="text-danger ml-2">*</span>
            {materialLoading && <span className="spinner-border spinner-border-sm text-primary ml-2" />}
          </div>

          <div className="d-flex align-items-center mb-4">
            <label className="mb-0 mr-3 text-nowrap" style={{ minWidth: '8.5rem' }}>Frame Diagnosis:</label>
            <Field
              name="frame_diagnosis"
              component={Select}
              data={FRAME_DIAGNOSIS_OPTIONS}
              textField="label"
              valueField="value"
              className="custom-select"
              wrapperClasses="mb-0 flex-grow-1"
              validate={[required]}
            />
            <span className="text-danger ml-2">*</span>
          </div>

          <div className="d-flex align-items-start">
            <label className="mb-0 mr-3" style={{ minWidth: '8.5rem' }}>
              Other Diagnosis:
              <br />
              <span className="text-muted small">(Specify ICD Codes separated by a comma)</span>
            </label>
            <Field name="other_diagnosis" component={Textarea} className="form-control" wrapperClasses="mb-0 flex-grow-1" rows={2} />
          </div>
        </div>
      </div>

      {showSpecialParams && (
        <SpecialParametersFields loading={specialLoading} fields={specialParams} />
      )}
    </>
  )
}

const selector = formValueSelector(FORM)

export default connect((state) => {
  const claimForm = state.visionInsuranceClaimForm.visionInsuranceClaimForm || {}
  return {
    claimId: claimForm.id,
    lensType: selector(state, 'lenses.lens_type'),
    lensDesign: selector(state, 'lenses.lens_design'),
    lensMaterial: selector(state, 'lenses.lens_material'),
    progressiveManufacturer: selector(state, 'lenses.progressive_manufacturer'),
    progressiveProduct: selector(state, 'lenses.progressive_product'),
    laboratory: selector(state, 'lenses.laboratory'),
    availableLabs: state.visionInsuranceClaimForm.eyemedAvailableLabs || [],
  }
})(LensDesignMaterialFields)
